import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Properties() {
  const [props_, setProps] = useState(null);
  const [q, setQ] = useState("");
  const [owners, setOwners] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", owner_id: "", lat: "", lng: "", access_notes: "", is_str: false });
  const [params, setParams] = useSearchParams();
  const snowFilter = params.get("snow") === "1";
  const [snowIds, setSnowIds] = useState(null);

  const load = () => {
    api.get("/properties").then((r) => setProps(r.data)).catch(() => toast.error("Could not load"));
    api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); if (params.get("snow") === "1") api.get("/agreements/active-snow").then((r) => setSnowIds(r.data.property_ids || [])).catch(() => {}); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", { ...form, lat: form.lat ? Number(form.lat) : null, lng: form.lng ? Number(form.lng) : null });
      toast.success("Property added");
      setShowAdd(false);
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const dismissClash = async (e, pid) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/properties/${pid}/pin`, { dismiss_clash: true });
      toast.success("Pin clash dismissed — it resets if the pin moves again");
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  return (
    <Layout title="Properties">
      <button data-testid="add-property-btn" onClick={() => setShowAdd(!showAdd)} className="mb-4 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary font-bold text-primary-foreground">
        <Plus size={20} weight="bold" /> Add property
      </button>
      <Link to="/properties/map" data-testid="map-audit-btn"
        className="mb-4 flex h-12 w-full items-center justify-center gap-2 rounded-md border-2 border-primary text-sm font-bold text-primary">
        Map audit — check every pin at once
      </Link>
      {showAdd && (
        <form onSubmit={add} className="card-flat mb-6 space-y-3 p-4" data-testid="add-property-form">
          <input data-testid="prop-name" className="field-input w-full" placeholder="Property name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <input data-testid="prop-address" className="field-input w-full" placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
          <p className="label-caps">Owner</p>
          <div className="flex flex-wrap gap-2">
            {owners.map((o) => (
              <button type="button" key={o.id} data-testid={`owner-pick-${o.id}`} onClick={() => setForm({ ...form, owner_id: o.id })}
                className={`h-12 rounded-md border px-4 text-sm font-bold ${form.owner_id === o.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{o.name}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="prop-lat" className="field-input flex-1" placeholder="Lat (optional)" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
            <input data-testid="prop-lng" className="field-input flex-1" placeholder="Lng (optional)" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
          </div>
          <input data-testid="prop-access" className="field-input w-full" placeholder="Access notes (gate codes, pets…)" value={form.access_notes} onChange={(e) => setForm({ ...form, access_notes: e.target.value })} />
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="prop-str" type="checkbox" checked={form.is_str} onChange={(e) => setForm({ ...form, is_str: e.target.checked })} className="h-5 w-5" /> Short-term rental
          </label>
          <button data-testid="prop-save" disabled={!form.owner_id} className="btn-primary-big !h-14 !text-base disabled:opacity-40">Save</button>
        </form>
      )}
      <input data-testid="search-input" className="field-input mb-4 w-full" placeholder="Search property, address, owner…" value={q} onChange={(e) => setQ(e.target.value)} />
      {props_ && props_.filter((p) => p.lat == null || p.lng == null).length > 0 && (
        <div className="mb-4 rounded-md border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/30" data-testid="geocode-health">
          <p className="text-sm font-bold text-amber-700 dark:text-amber-400">
            {props_.filter((p) => p.lat == null || p.lng == null).length} of {props_.length} properties have no GPS pin — check-ins there can't verify location
          </p>
          <p className="text-xs text-muted-foreground">Open each property and drop the pin on the map — geocoding retries daily in the background too.</p>
        </div>
      )}
      {props_ && props_.filter((p) => p.pin_clustered).length > 0 && (
        <div className="mb-4 rounded-md border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/30" data-testid="pin-cluster-warning">
          <p className="text-sm font-bold text-amber-700 dark:text-amber-400">
            {props_.filter((p) => p.pin_clustered).length} properties have pins within 50m of another property — likely street-centroid geocodes
          </p>
          <p className="text-xs text-muted-foreground">Open the map audit to eyeball them, then drop exact pins.</p>
        </div>
      )}
      {props_ && props_.filter((p) => p.pin_unverified).length > 0 && (
        <div className="mb-4 rounded-md border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/30" data-testid="pin-unverified-warning">
          <p className="text-sm font-bold text-amber-700 dark:text-amber-400">
            {props_.filter((p) => p.pin_unverified).length} properties have geocoded pins nobody has verified — their check-in radius stays at 300m until you confirm each pin
          </p>
        </div>
      )}
      {snowFilter && (
        <div className="mb-4 rounded-md border border-primary bg-primary/5 p-3" data-testid="snow-agreements-filter-banner">
          <p className="text-sm font-bold text-primary">Showing {(snowIds || []).length} {(snowIds || []).length === 1 ? "property" : "properties"} with an active snow agreement</p>
          <p className="text-xs text-muted-foreground">Open a property to pause or end its agreement — that is what stops snow billing.</p>
          <button data-testid="snow-filter-clear" onClick={() => setParams({})} className="mt-1 text-xs font-bold text-primary underline">Show all properties</button>
        </div>
      )}
      <div className="space-y-2" data-testid="property-list">
        {!props_ && <p className="text-muted-foreground">Loading…</p>}
        {props_?.filter((p) => !snowFilter || (snowIds || []).includes(p.id)).filter((p) => !q || [p.name, p.address, p.owner_name].join(" ").toLowerCase().includes(q.toLowerCase())).map((p) => (
          <Link key={p.id} to={`/properties/${p.id}`} data-testid={`property-row-${p.id}`}>
            <div className="card-flat p-4 transition-colors hover:bg-muted/50">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{p.name}</p>
                <div className="flex flex-wrap gap-1">
                  {p.active === false && <span className="rounded-full bg-destructive/10 px-2 py-1 text-xs font-bold text-destructive">Inactive</span>}
                  {p.is_str && <span className="rounded-full bg-secondary px-2 py-1 text-xs font-bold text-secondary-foreground">STR</span>}
                  {(p.lat == null || p.lng == null) && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-700 dark:bg-amber-950 dark:text-amber-400" data-testid={`no-pin-badge-${p.id}`}>No GPS pin</span>}
                  {p.pin_unverified && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-700 dark:bg-amber-950 dark:text-amber-400" data-testid={`unverified-badge-${p.id}`}>Unverified pin</span>}
                  {p.pin_clustered && (
                    <span className="flex items-center gap-1">
                      <span className="rounded-full bg-destructive/10 px-2 py-1 text-xs font-bold text-destructive" data-testid={`cluster-badge-${p.id}`}>Pin clash</span>
                      <button data-testid={`dismiss-clash-${p.id}`} onClick={(e) => dismissClash(e, p.id)}
                        className="rounded-full border border-border px-2 py-1 text-xs font-bold text-muted-foreground hover:bg-muted">Dismiss</button>
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{p.address} · {p.owner_name}
                {p.owner_missing && (
                  <span className="ml-2 rounded bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive" data-testid={`owner-missing-${p.id}`}>
                    No owner — assign one before invoicing
                  </span>
                )}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </Layout>
  );
}
